import type { TaxonomyUploadPayload } from '../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';

/** Wizard draft mapped to Mongo `config` (banking WhatsApp example). */
export interface SentimentSetupDraft {
  industryName: string;
  industryDescription: string;
  taxonomy: TaxonomyUploadPayload | null;
  /** `config.stats.widgets.heatmap_v2.cohorts` */
  heatmapCohortFieldIds: string[];
  /** `config.summary.precompute.supported_cohorts` */
  summaryCohortFieldIds: string[];
  cohortLabels: Record<string, string>;
  widgetEnabled: Record<string, boolean>;
  themeDomainInstruction: string;
  taxonomyThemeMode: string;
  taxonomySubthemeMode: string;
  summaryPrecomputeEnabled: boolean;
  /** `config.summary.precompute.sentiments` — e.g. positive, negative, neutral. */
  summaryPrecomputeSentiments: string[];
  /** `config.summary.precompute.theme_allowlist` — subset of taxonomy theme names. */
  summaryThemeAllowlist: string[];
}

/** Allowed values for `summary.precompute.sentiments` (excludes aggregate `all` for setup UI). */
export const SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES = ['positive', 'negative', 'neutral'] as const;

const SUMMARY_SENTIMENT_LABELS: Record<string, string> = {
  positive: 'Positive',
  negative: 'Negative',
  neutral: 'Neutral',
};

export function summarySentimentLabel(sentimentId: string): string {
  const id = String(sentimentId ?? '').trim().toLowerCase();
  return SUMMARY_SENTIMENT_LABELS[id] ?? id;
}

export function normalizeSummaryPrecomputeSentiments(
  values: unknown,
  fallback: string[] = ['positive', 'negative'],
): string[] {
  const allowed = new Set<string>(SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES);
  const raw = Array.isArray(values) ? values : fallback;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of raw) {
    const id = String(v ?? '').trim().toLowerCase();
    if (!id || id === 'all' || !allowed.has(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out.length ? out : normalizeSummaryPrecomputeSentiments(fallback, ['positive', 'negative']);
}

export function toggleSummaryPrecomputeSentiment(current: string[], sentimentId: string): string[] {
  const id = String(sentimentId ?? '').trim().toLowerCase();
  if (!SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES.includes(id as (typeof SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES)[number])) {
    return normalizeSummaryPrecomputeSentiments(current);
  }
  const normalized = normalizeSummaryPrecomputeSentiments(current);
  const set = new Set(normalized);
  if (set.has(id)) {
    if (set.size <= 1) {
      return normalized;
    }
    set.delete(id);
  } else {
    set.add(id);
  }
  return normalizeSummaryPrecomputeSentiments([...set]);
}

export function summaryPrecomputeSentimentsFromProjectConfig(
  config: Record<string, unknown> | null | undefined,
): string[] {
  const summary = config?.['summary'] as Record<string, unknown> | undefined;
  const precompute = summary?.['precompute'] as Record<string, unknown> | undefined;
  return normalizeSummaryPrecomputeSentiments(precompute?.['sentiments']);
}

export function summaryGeneratorSentimentOptionsFromSentiments(
  sentimentIds: string[],
): Array<{ value: string; label: string }> {
  return normalizeSummaryPrecomputeSentiments(sentimentIds).map((value) => ({
    value,
    label: summarySentimentLabel(value),
  }));
}

export function taxonomyThemeNamesFromDraft(draft: SentimentSetupDraft): string[] {
  const themes = draft.taxonomy?.themes ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of themes) {
    const name = String(t.name ?? '').trim();
    if (!name || seen.has(name)) {
      continue;
    }
    seen.add(name);
    out.push(name);
  }
  return out;
}

export type RatingAlignmentScale = 'nps' | 'csat';

/**
 * NPS: `main_rater_type` 3, 10, or 11. CSAT: 4. Otherwise not configured (no default).
 */
export function resolveRatingAlignmentFromMainRaterType(
  mainRaterType: unknown,
): RatingAlignmentScale | null {
  if (mainRaterType == null || mainRaterType === '') {
    return null;
  }
  const n = Number(mainRaterType);
  if (!Number.isFinite(n)) {
    return null;
  }
  if (n === 4) {
    return 'csat';
  }
  if (n === 3 || n === 10 || n === 11) {
    return 'nps';
  }
  return null;
}

/** @deprecated Use `resolveRatingAlignmentFromMainRaterType`; throws when not configured. */
export function ratingAlignmentScaleFromMainRaterType(mainRaterType: unknown): RatingAlignmentScale {
  const scale = resolveRatingAlignmentFromMainRaterType(mainRaterType);
  if (!scale) {
    throw new Error('Project primary rating type (NPS or CSAT) is not configured');
  }
  return scale;
}

export function cohortFieldToEsKeywordPath(fieldId: string): string {
  const id = String(fieldId ?? '').trim();
  if (!id || id === 'rating_bucket') {
    return '';
  }
  return `litmus_processed_data.${id}.keyword`;
}

export function buildHeatmapV2Cohorts(fieldIds: string[]): Record<string, string> {
  const cohorts: Record<string, string> = {};
  for (const id of fieldIds) {
    const path = cohortFieldToEsKeywordPath(id);
    if (path) {
      cohorts[id] = path;
    }
  }
  return cohorts;
}

export function buildSupportedCohorts(
  fieldIds: string[],
  labels: Record<string, string>,
): Array<{ key: string; label: string; field: string }> {
  return fieldIds
    .map((id) => {
      const field = cohortFieldToEsKeywordPath(id);
      if (!field) {
        return null;
      }
      return {
        key: id,
        label: String(labels[id] ?? id).trim() || id,
        field,
      };
    })
    .filter((x): x is { key: string; label: string; field: string } => x != null);
}

export function defaultWidgetEnabled(): Record<string, boolean> {
  return {
    overview: true,
    theme_distribution: true,
    subtheme_distribution: true,
    theme_detail: true,
    subtheme_detail: true,
    quick_picks: true,
    theme_trend: true,
    subtheme_trend: true,
    sentiment_trend: true,
    heatmap: true,
    cohort_breakdown: true,
  };
}

export function createEmptySetupDraft(): SentimentSetupDraft {
  return {
    industryName: '',
    industryDescription: '',
    taxonomy: null,
    heatmapCohortFieldIds: [],
    summaryCohortFieldIds: [],
    cohortLabels: {},
    widgetEnabled: defaultWidgetEnabled(),
    themeDomainInstruction: '',
    taxonomyThemeMode: 'names_descriptions_and_keywords',
    taxonomySubthemeMode: 'names_descriptions_and_keywords',
    summaryPrecomputeEnabled: true,
    summaryPrecomputeSentiments: ['positive', 'negative'],
    summaryThemeAllowlist: [],
  };
}

/** Hydrate wizard/settings draft from GET `latest_sentiment_config` `config`. */
export function setupDraftFromProjectConfig(
  config: Record<string, unknown> | null | undefined,
): SentimentSetupDraft {
  const draft = createEmptySetupDraft();
  if (!config || typeof config !== 'object') {
    return draft;
  }

  const industry = config['industry'] as Record<string, unknown> | undefined;
  if (industry) {
    draft.industryName = String(industry['name'] ?? '').trim();
    draft.industryDescription = String(industry['description'] ?? '').trim();
  }

  const taxonomy = config['taxonomy'] as Record<string, unknown> | undefined;
  const themes = taxonomy?.['themes'];
  if (Array.isArray(themes) && themes.length > 0) {
    draft.taxonomy = {
      mode: String(taxonomy?.['mode'] ?? 'optional'),
      themes: themes as TaxonomyUploadPayload['themes'],
    };
  }

  const stats = config['stats'] as Record<string, unknown> | undefined;
  const widgets = stats?.['widgets'] as Record<string, unknown> | undefined;
  const heatmapV2 = widgets?.['heatmap_v2'] as Record<string, unknown> | undefined;
  const cohorts = heatmapV2?.['cohorts'] as Record<string, string> | undefined;
  if (cohorts && typeof cohorts === 'object') {
    draft.heatmapCohortFieldIds = Object.keys(cohorts).filter((id) => String(cohorts[id] ?? '').trim());
  }

  const summary = config['summary'] as Record<string, unknown> | undefined;
  const precompute = summary?.['precompute'] as Record<string, unknown> | undefined;
  if (precompute) {
    draft.summaryPrecomputeEnabled = precompute['enabled'] !== false;
    const supported = precompute['supported_cohorts'];
    if (Array.isArray(supported)) {
      const ids: string[] = [];
      const labels: Record<string, string> = { ...draft.cohortLabels };
      for (const item of supported) {
        if (!item || typeof item !== 'object') {
          continue;
        }
        const row = item as Record<string, unknown>;
        const key = String(row['key'] ?? '').trim();
        if (!key) {
          continue;
        }
        ids.push(key);
        labels[key] = String(row['label'] ?? key).trim() || key;
      }
      draft.summaryCohortFieldIds = ids;
      draft.cohortLabels = labels;
    }
    draft.summaryPrecomputeSentiments = normalizeSummaryPrecomputeSentiments(precompute['sentiments']);
    const allowlist = precompute['theme_allowlist'];
    if (Array.isArray(allowlist)) {
      draft.summaryThemeAllowlist = allowlist.map((n) => String(n).trim()).filter(Boolean);
    }
  }

  if (widgets) {
    const enabled = { ...defaultWidgetEnabled() };
    for (const key of Object.keys(enabled)) {
      const w = widgets[key] as Record<string, unknown> | undefined;
      if (w && typeof w === 'object' && 'enabled' in w) {
        enabled[key] = w['enabled'] !== false;
      }
    }
    draft.widgetEnabled = enabled;
  }

  const prompt = config['prompt'] as Record<string, unknown> | undefined;
  if (prompt) {
    const themeMode = prompt['taxonomy_theme_mode'];
    if (themeMode != null && String(themeMode).trim()) {
      draft.taxonomyThemeMode = String(themeMode);
    }
    const subMode = prompt['taxonomy_subtheme_mode'];
    if (subMode != null && String(subMode).trim()) {
      draft.taxonomySubthemeMode = String(subMode);
    }
    const steps = prompt['steps'] as Record<string, unknown> | undefined;
    const themeStep = steps?.['theme'] as Record<string, unknown> | undefined;
    if (themeStep) {
      draft.themeDomainInstruction = String(themeStep['domain_instruction'] ?? '').trim();
    }
  }

  return draft;
}

/** Rating scale stored on saved config when brand `main_rater_type` is unavailable in UI. */
export function resolveRatingAlignmentFromProjectConfig(
  config: Record<string, unknown> | null | undefined,
): RatingAlignmentScale | null {
  const ai = config?.['analysis_input'] as Record<string, unknown> | undefined;
  const scale = String(ai?.['rating_alignment_scale'] ?? '').trim().toLowerCase();
  if (scale === 'nps' || scale === 'csat') {
    return scale;
  }
  return null;
}

/** Build partial `config` for POST `/api/config/v1/project_config_save`. */
export function buildProjectConfigFromDraft(
  draft: SentimentSetupDraft,
  ratingAlignmentScale: 'nps' | 'csat',
): Record<string, unknown> {
  const taxonomy = draft.taxonomy;
  if (!taxonomy?.themes?.length) {
    throw new Error('Taxonomy is required before saving configuration');
  }

  const taxonomyThemeNames = taxonomyThemeNamesFromDraft(draft);
  const allowlistRaw = draft.summaryThemeAllowlist ?? [];
  const allowlistSet = new Set(allowlistRaw.map((n) => String(n).trim()).filter(Boolean));
  const themeAllowlist =
    allowlistSet.size > 0
      ? taxonomyThemeNames.filter((n) => allowlistSet.has(n))
      : taxonomyThemeNames;

  const cohorts = buildHeatmapV2Cohorts(draft.heatmapCohortFieldIds);
  const supportedCohorts = buildSupportedCohorts(draft.summaryCohortFieldIds, draft.cohortLabels);

  const widgets: Record<string, unknown> = {
    heatmap_v2: { cohorts },
  };
  for (const [key, enabled] of Object.entries(draft.widgetEnabled)) {
    widgets[key] = { enabled: !!enabled };
  }

  return {
    industry: {
      name: String(draft.industryName ?? '').trim() || 'generic',
      description: String(draft.industryDescription ?? '').trim(),
      actionability_hints: [],
      sentiment_hints: [],
    },
    taxonomy: {
      mode: taxonomy.mode || 'optional',
      themes: taxonomy.themes,
    },
    analysis_input: {
      project_scope_field: 'brand_id',
      feed_id_field: '_id',
      comment_text_field: 'feed_params.comments',
      rating_alignment_scale: ratingAlignmentScale,
      include_nps_score: true,
      nps_score_field: 'feed_params.primary_rating',
      include_metadata: false,
      metadata_field: 'metadata',
      include_created_at: false,
      created_at_field: 'created_date',
    },
    prompt: {
      include_metadata: false,
      metadata_fields: [],
      include_nps_in_actionability: false,
      include_nps_in_sentiment: true,
      industry_mode: 'name_only',
      include_industry_hints: false,
      include_glossary: false,
      include_examples: false,
      taxonomy_theme_mode: draft.taxonomyThemeMode,
      taxonomy_subtheme_mode: draft.taxonomySubthemeMode,
      include_taxonomy_examples: false,
      max_comment_chars_per_step: {
        actionability: 3000,
        sentiment: 3000,
        theme: 4000,
        subtheme: 2500,
      },
      steps: {
        actionability: { domain_instruction: '', tone: 'operational' },
        sentiment: { domain_instruction: '', tone: 'balanced' },
        theme: {
          tone: 'strict',
          domain_instruction: String(draft.themeDomainInstruction ?? '').trim(),
        },
        subtheme: {
          tone: 'strict',
          domain_instruction:
            'Pick exactly one subtheme from the selected theme list. Use Others only when no listed subtheme fits. Do not invent a new top-level theme name.',
        },
        summary: { domain_instruction: '', tone: 'customer_experience' },
        recommendation: { domain_instruction: '', tone: 'operational' },
      },
    },
    summary: {
      only_actionable_default: true,
      max_source_scan: 1000,
      max_chars_per_comment_in_prompt: 400,
      max_focus_areas: 5,
      max_evidence_phrases: 5,
      sampling: {
        max_total_comments: 30,
        small_max: 10,
        medium_max: 10,
        large_max: 10,
      },
      precompute: {
        enabled: draft.summaryPrecomputeEnabled,
        sentiments: normalizeSummaryPrecomputeSentiments(draft.summaryPrecomputeSentiments),
        max_themes: 10,
        supported_cohorts: supportedCohorts,
        theme_source: { mode: 'allowlist' },
        theme_allowlist: themeAllowlist,
      },
    },
    stats: {
      allowed_field_filters: [],
      es: { index: 'litmus_app_feed_1_' },
      widgets,
    },
  };
}

export interface BrandCohortFieldOption {
  fieldId: string;
  label: string;
  /** Brand already has `show_in_sentiment_cohort` — pre-check in setup wizard. */
  enabledOnBrand: boolean;
}

/** Same rules as Sentiment Settings → Cohort dimensions tab. */
export function isEligibleCohortTagFieldId(fieldId: string): boolean {
  if (!fieldId.startsWith('tag_')) {
    return false;
  }
  if (fieldId === 'tag_feedback_reference_token' || fieldId === 'tag_selected_language_key') {
    return false;
  }
  if (fieldId.startsWith('tag_private_')) {
    return false;
  }
  return true;
}

function cohortFieldLabelFromDef(fd: Record<string, unknown>, fieldId: string): string {
  const label = String(
    fd['field_display_name'] ??
      fd['fieldDisplayName'] ??
      fd['display_name'] ??
      fd['displayName'] ??
      fd['name'] ??
      fd['label'] ??
      fieldId,
  ).trim();
  return label || fieldId;
}

function fieldDefinitionsList(brandDetails: Record<string, unknown> | null | undefined): unknown[] {
  if (!brandDetails || typeof brandDetails !== 'object') {
    return [];
  }
  const direct = brandDetails['field_definitions'];
  if (Array.isArray(direct)) {
    return direct;
  }
  const nested = brandDetails['brand'] as Record<string, unknown> | undefined;
  if (nested && Array.isArray(nested['field_definitions'])) {
    return nested['field_definitions'];
  }
  return [];
}

/** Runtime Category Heatmap cohort dropdown (`value` = `field_id` from `heatmap_v2.cohorts`). */
export interface HeatmapCohortFilterOptionFromConfig {
  value: string;
  label: string;
}

const HEATMAP_RATING_BUCKET_OPTION: HeatmapCohortFilterOptionFromConfig = {
  value: 'rating_bucket',
  label: 'Rating Bucket',
};

/** Always present on the heatmap cohort dropdown (appended last when missing from config). */
export function appendHeatmapRatingBucketDefault(
  options: HeatmapCohortFilterOptionFromConfig[],
): HeatmapCohortFilterOptionFromConfig[] {
  if (options.some((o) => o.value === 'rating_bucket')) {
    return options;
  }
  return [...options, HEATMAP_RATING_BUCKET_OPTION];
}

/**
 * Category Heatmap Analysis cohort dropdown: keys from `config.stats.widgets.heatmap_v2.cohorts`
 * plus Rating Bucket (always included as the default cohort option).
 */
export function heatmapCohortFilterOptionsFromProjectConfig(
  config: Record<string, unknown> | null | undefined,
  brandDetails: Record<string, unknown> | null | undefined,
): HeatmapCohortFilterOptionFromConfig[] {
  const stats = config?.['stats'] as Record<string, unknown> | undefined;
  const widgets = stats?.['widgets'] as Record<string, unknown> | undefined;
  const heatmapV2 = widgets?.['heatmap_v2'] as Record<string, unknown> | undefined;
  const cohorts = heatmapV2?.['cohorts'] as Record<string, string> | undefined;

  const labelByFieldId = new Map<string, string>();
  for (const raw of fieldDefinitionsList(brandDetails)) {
    const fd = raw as Record<string, unknown>;
    if (!fd || typeof fd !== 'object') {
      continue;
    }
    const fieldId = String(fd['field_id'] ?? fd['fieldId'] ?? '').trim();
    if (fieldId) {
      labelByFieldId.set(fieldId, cohortFieldLabelFromDef(fd, fieldId));
    }
  }

  const out: HeatmapCohortFilterOptionFromConfig[] = [];
  const seen = new Set<string>();
  if (cohorts && typeof cohorts === 'object') {
    for (const fieldId of Object.keys(cohorts)) {
      const value = String(fieldId ?? '').trim();
      if (!value || seen.has(value) || value === 'rating_bucket') {
        continue;
      }
      if (!String(cohorts[fieldId] ?? '').trim()) {
        continue;
      }
      seen.add(value);
      out.push({
        value,
        label: labelByFieldId.get(value) ?? value,
      });
    }
  }

  return appendHeatmapRatingBucketDefault(out);
}

/**
 * All eligible `tag_*` cohort fields for setup (not only `show_in_sentiment_cohort`).
 */
export function cohortFieldOptionsFromBrandDetails(
  brandDetails: Record<string, unknown> | null | undefined,
): BrandCohortFieldOption[] {
  const list = fieldDefinitionsList(brandDetails);
  const seen = new Set<string>();
  const out: BrandCohortFieldOption[] = [];

  for (const raw of list) {
    const fd = raw as Record<string, unknown>;
    if (!fd || typeof fd !== 'object') {
      continue;
    }
    const fieldId = String(fd['field_id'] ?? fd['fieldId'] ?? '').trim();
    if (!fieldId || seen.has(fieldId) || !isEligibleCohortTagFieldId(fieldId)) {
      continue;
    }
    seen.add(fieldId);
    const flag = fd['show_in_sentiment_cohort'] ?? fd['showInSentimentCohort'];
    const enabledOnBrand = flag === true || flag === 'true' || flag === 1;
    out.push({
      fieldId,
      label: cohortFieldLabelFromDef(fd, fieldId),
      enabledOnBrand,
    });
  }
  return out;
}

/** Merge `field_definitions` from AppState / Helpers when the input snapshot is missing them. */
export function resolveBrandDetailsForCohortSetup(
  input: Record<string, unknown> | null | undefined,
  appStateBrand: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  let bd =
    input && typeof input === 'object'
      ? input
      : appStateBrand && typeof appStateBrand === 'object'
        ? appStateBrand
        : null;
  if (!bd) {
    return null;
  }
  if (fieldDefinitionsList(bd).length > 0) {
    return bd;
  }
  const H =
    typeof Helpers !== 'undefined'
      ? (Helpers as { brandDetails?: Record<string, unknown> })
      : (globalThis as { Helpers?: { brandDetails?: Record<string, unknown> } }).Helpers;
  const helpersBd = H?.brandDetails;
  const helpersDefs = helpersBd?.['field_definitions'];
  if (Array.isArray(helpersDefs) && helpersDefs.length) {
    return { ...bd, field_definitions: helpersDefs };
  }
  if (
    appStateBrand &&
    typeof appStateBrand === 'object' &&
    appStateBrand !== bd &&
    fieldDefinitionsList(appStateBrand).length > 0
  ) {
    return { ...bd, field_definitions: fieldDefinitionsList(appStateBrand) };
  }
  return bd;
}
